/**
 * Request payload construction for EntityForm submissions.
 *
 * Builds the API request payload by filtering hidden fields, applying
 * request mappings, and converting to FormData when file uploads are present.
 *
 * @see spec section 7.3
 */

import type { EntityFormConfig } from '../types';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Builds the API request payload from form values.
 *
 * 1. Filters out hidden fields using the visibility map.
 * 2. Applies `requestMapping` from the endpoint config.
 * 3. If any visible field is of type `file`, wraps in FormData.
 * 4. Returns either a plain object or FormData.
 *
 * @param values        - Current form values keyed by field name.
 * @param config        - The full EntityForm configuration.
 * @param visibilityMap - Map of field name to visibility boolean.
 * @returns A plain object or FormData ready for the API call.
 */
export function buildPayload(
  values: Record<string, unknown>,
  config: EntityFormConfig,
  visibilityMap: Record<string, boolean>,
): Record<string, unknown> | FormData {
  // 1. Filter out hidden fields
  const visibleValues: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (visibilityMap[key]) {
      visibleValues[key] = value;
    }
  }

  // 2. Apply requestMapping
  const mapped = applyMapping(visibleValues, config.endpoint.requestMapping);

  // 3. Check for file fields among visible fields
  const hasFiles = Object.keys(config.fields).some((key) => {
    const fieldConfig = config.fields[key];
    return fieldConfig?.type === 'file' && visibilityMap[key] === true;
  });

  if (hasFiles) {
    return buildFormData(mapped);
  }

  return mapped;
}

// ---------------------------------------------------------------------------
// Mapping
// ---------------------------------------------------------------------------

/**
 * Applies key mapping to a values object.
 *
 * For each entry in `mapping`, the form field name (key) is replaced with
 * the API payload path (value). Fields without a mapping entry are passed
 * through unchanged.
 *
 * @param values  - The values object to map.
 * @param mapping - Optional mapping of form field name to API payload key.
 * @returns A new object with mapped keys.
 */
export function applyMapping(
  values: Record<string, unknown>,
  mapping?: Record<string, string>,
): Record<string, unknown> {
  if (!mapping) {
    return { ...values };
  }

  const result: Record<string, unknown> = {};

  // Build a reverse lookup: form field name -> api field name
  // mapping is { formField: apiField }
  for (const [key, value] of Object.entries(values)) {
    const mappedKey = mapping[key];
    if (mappedKey === undefined) {
      result[key] = value;
    } else {
      setNestedValue(result, mappedKey, value);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// FormData
// ---------------------------------------------------------------------------

/**
 * Converts a flat object to FormData.
 *
 * Handles:
 * - `File` instances are appended directly.
 * - Arrays of `File` instances are appended individually under the same key.
 * - Other arrays and objects are serialised as JSON strings.
 * - Primitives (string, number, boolean) are converted to strings.
 * - `null` and `undefined` values are skipped.
 *
 * @param mapped - The mapped payload object.
 * @returns A FormData instance.
 */
export function buildFormData(mapped: Record<string, unknown>): FormData {
  const formData = new FormData();

  for (const [key, value] of Object.entries(mapped)) {
    appendFormDataEntry(formData, key, value);
  }

  return formData;
}

/**
 * Appends a single key/value entry to a FormData instance.
 *
 * Skips null/undefined values. Files are appended directly, arrays of
 * files are appended individually, other arrays and objects are JSON-serialised,
 * and primitives are converted to strings.
 */
function appendFormDataEntry(
  formData: FormData,
  key: string,
  value: unknown,
): void {
  if (value === null || value === undefined) {
    return;
  }

  if (value instanceof File) {
    formData.append(key, value);
    return;
  }

  if (Array.isArray(value)) {
    appendArrayToFormData(formData, key, value);
    return;
  }

  if (typeof value === 'object') {
    // Nested objects are serialised as JSON
    formData.append(key, JSON.stringify(value));
    return;
  }

  // Primitives: string, number, boolean
  formData.append(key, String(value as string | number | boolean));
}

/**
 * Appends an array value to FormData.
 *
 * If the array contains File objects, each file is appended individually
 * under the same key. Otherwise the array is serialised as a JSON string.
 */
function appendArrayToFormData(
  formData: FormData,
  key: string,
  value: unknown[],
): void {
  const isFileArray = value.length > 0 && value.every((item) => item instanceof File);

  if (isFileArray) {
    for (const file of value) {
      formData.append(key, file);
    }
  } else {
    // Serialise non-file arrays as JSON
    formData.append(key, JSON.stringify(value));
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns whether the given value is a plain (non-array) object.
 */
function isPlainObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

/**
 * Resolves or creates the intermediate object for a single path segment,
 * returning the next level of nesting.
 */
function resolveSegment(
  current: Record<string, unknown>,
  segment: string,
): Record<string, unknown> {
  const existing = current[segment];
  if (isPlainObject(existing)) {
    return existing;
  }
  const nested: Record<string, unknown> = {};
  current[segment] = nested;
  return nested;
}

/**
 * Sets a value at a dot-notation path on an object, creating intermediate
 * objects as needed.
 *
 * @example
 * ```ts
 * const obj = {};
 * setNestedValue(obj, 'data.user.name', 'Alice');
 * // obj === { data: { user: { name: 'Alice' } } }
 * ```
 */
function setNestedValue(
  target: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const segments = path.split('.');

  if (segments.length === 1) {
    target[path] = value;
    return;
  }

  let current: Record<string, unknown> = target;

  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    // noUncheckedIndexedAccess: segment is always defined in a valid for loop
    /* v8 ignore start */
    if (segment === undefined) {
      return;
    }
    /* v8 ignore stop */
    current = resolveSegment(current, segment);
  }

  const lastSegment = segments.at(-1);
  /* v8 ignore start */
  if (lastSegment !== undefined) {
    /* v8 ignore stop */
    current[lastSegment] = value;
  }
}
